// src/components/DirectoryNode/DirectoryNode.jsx
import React from 'react';
import {
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Collapse,
    CircularProgress,
    Box,
} from '@mui/material';
import {
    Folder as FolderIcon,
    ExpandLess,
    ExpandMore,
    PlayCircleOutline
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import DocCardImage from '../DocCardImage/DocCardImage';

const DirectoryNode = ({
    node,
    level = 0,
    expandedFolders = {},
    onFolderToggle,
    loadingChildren = {},
    isCBTFolder,
    onFileClick,
    onFileOpen
}) => {
    const isExpanded = expandedFolders[node.path] || false;
    const isLoading = loadingChildren[node.path] || false;
    const hasChildren = Array.isArray(node.children) && node.children.length > 0;
    const isCBT = isCBTFolder && isCBTFolder(node.path);
    const isVideoFile = node.type === 'file' && (node.name || node.path || '').toLowerCase().endsWith('.mp4');
    const { t } = useTranslation();

    const handleRowClick = () => {
        if (node.type === 'folder') {
            onFolderToggle(node.path);
        } else if (node.type === 'file') {
            if (onFileClick) onFileClick(node.path);
            else if (onFileOpen) onFileOpen(node.path);
        }
    };

    const getFolderIcon = () => (
        isCBT ? (
            <PlayCircleOutline sx={{ color: '#d60039', fontSize: 20 }} />
        ) : (
            <FolderIcon sx={{ color: '#909090', fontSize: 20 }} />
        )
    );

    const getDisplayName = (node) => {
        if (node.type !== 'folder') return node.name;

        const normalized = node.name
            .toLowerCase()
            .replace(/\s+/g, '')
            .replace(/-/g, '');

        // Any variation containing CBT but starting with B
        if (/^b.*cbt$/.test(normalized) || normalized === 'cbt') {
            return 'Basic Computer Based Training';
        }

        // Any variation containing VBT
        if (normalized.includes('vbt')) {
            return 'Basic Video Based Training';
        }

        return node.name;
    };

    /**
     * Renders the appropriate file icon using DocCardImage.
     * DocCardImage accepts `path` and will infer the extension from it,
     * falling back to the default icon if the type is unrecognised.
     */
    const getFileIcon = () => (
        isVideoFile ? (
            <PlayCircleOutline sx={{ color: '#d60039', fontSize: 20 }} />
        ) : (
            <DocCardImage
                path={node.path}
                filename={node.name}
                fileExtension={node.fileExtension}   // pass through if your tree node carries it
                type={node.mimeType}                  // pass through if your tree node carries it
            />
        )
    );

    const getFolderTextStyle = () => ({
        color: '#d60039',
        fontSize: 13,
        fontWeight: isCBT ? 600 : 500
    });

    const getFileTextStyle = () => ({
        color: 'text.primary',
        fontSize: 13,
        fontWeight: 500
    });

    const getHoverStyle = () =>
        isCBT
            ? { bgcolor: 'rgba(214, 0, 57, 0.08)' }
            : { bgcolor: 'action.hover' };

    return (
        <>
            <ListItemButton
                onClick={handleRowClick}
                disabled={isLoading}
                sx={{
                    pl: `${12 + level * 16}px`,
                    py: 0.75,
                    minHeight: 36,
                    borderRadius: 1,
                    margin: '1px 0',
                    '&:hover': getHoverStyle()
                }}
            >
                {/* ── Icon column ── */}
                <ListItemIcon sx={{ minWidth: 36 }}>
                    {node.type === 'folder' ? getFolderIcon() : getFileIcon()}
                </ListItemIcon>

                {/* ── Label ── */}
                <ListItemText
                    primary={getDisplayName(node)}
                    primaryTypographyProps={{
                        variant: 'body2',
                        sx: node.type === 'folder' ? getFolderTextStyle() : getFileTextStyle()
                    }}
                />

                {/* ── Trailing indicator ── */}
                {isLoading ? (
                    <CircularProgress size={16} />
                ) : isCBT ? (
                    <PlayCircleOutline sx={{ fontSize: 18, color: '#d60039' }} />
                ) : hasChildren || node.type === 'folder' ? (
                    isExpanded ? <ExpandLess /> : <ExpandMore />
                ) : null}
            </ListItemButton>

            {/* ── Children (non-CBT folders only) ── */}
            {node.type === 'folder' && !isCBT && (
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <List disablePadding>
                        {isLoading ? (
                            <Box
                                sx={{
                                    pl: `${12 + (level + 1) * 16}px`,
                                    py: 1.5,
                                    textAlign: 'center'
                                }}
                            >
                                <CircularProgress size={16} />
                            </Box>
                        ) : Array.isArray(node.children) && node.children.length > 0 ? (
                            node.children.map(child => (
                                <DirectoryNode
                                    key={child.id || child.path}
                                    node={child}
                                    level={level + 1}
                                    expandedFolders={expandedFolders}
                                    onFolderToggle={onFolderToggle}
                                    loadingChildren={loadingChildren}
                                    isCBTFolder={isCBTFolder}
                                    onFileClick={onFileClick}
                                    onFileOpen={onFileOpen}
                                />
                            ))
                        ) : (
                            <Box
                                sx={{
                                    pl: `${12 + (level + 1) * 16}px`,
                                    py: 1.5,
                                    fontSize: 13,
                                    fontStyle: 'italic',
                                    color: 'text.secondary'
                                }}
                            >
                                {t('HISTORICAL_CONTENT.NO_FILES_OR_SUBFOLDERS')}
                            </Box>
                        )}
                    </List>
                </Collapse>
            )}
        </>
    );
};

export default DirectoryNode;
